import { useRef, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, Trash2, Loader2, Image as ImageIcon, ChevronDown } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
// Relative /api URLs are resolved against the backend so the image always loads.
export const resolveAsset = (url) =>
  url && url.startsWith("/api") ? `${BACKEND}${url}` : url;

const ACCEPT = ".png,.jpg,.jpeg,.svg,.webp,image/png,image/jpeg,image/svg+xml,image/webp";

export default function BrandingImageUpload({ kind, label, hint, aspect = "wide", tenant, setTenant, isAdmin }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const [showUrl, setShowUrl] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const current = tenant?.[`${kind}_url`] || "";
  const hasImage = !!current;

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      const { data } = await api.post("/branding/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTenant(data);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/branding/${kind}`);
      setTenant(data);
      toast.success(`${label} removed`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const applyUrl = async () => {
    if (!urlInput.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.put("/tenant", { [`${kind}_url`]: urlInput.trim(), [`${kind}_storage_path`]: null });
      setTenant(data); setUrlInput("");
      toast.success(`${label} URL applied`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const previewBox = aspect === "square" ? "h-20 w-20" : "h-20 w-full max-w-xs";

  return (
    <div className="rounded-lg border border-slate-200 p-4" data-testid={`branding-${kind}`}>
      <Label className="text-sm font-medium text-slate-700">{label}</Label>
      {hint && <p className="text-xs text-slate-400 mt-0.5">{hint}</p>}

      {/* Preview — aspect ratio preserved, never stretched or cropped */}
      <div className={`mt-3 ${previewBox} rounded-md border border-dashed border-slate-200 bg-[linear-gradient(45deg,#f1f5f9_25%,transparent_25%,transparent_75%,#f1f5f9_75%),linear-gradient(45deg,#f1f5f9_25%,#fff_25%,#fff_75%,#f1f5f9_75%)] bg-[length:16px_16px] bg-[position:0_0,8px_8px] flex items-center justify-center overflow-hidden`}>
        {hasImage ? (
          <img src={resolveAsset(current)} alt={label}
               className="max-h-full max-w-full object-contain" data-testid={`branding-${kind}-preview`} />
        ) : (
          <div className="flex flex-col items-center text-slate-300">
            <ImageIcon className="h-6 w-6" />
            <span className="text-[11px] mt-1">No {label.toLowerCase()}</span>
          </div>
        )}
      </div>

      {isAdmin && (
        <>
          <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={onPick}
                 data-testid={`branding-${kind}-input`} />
          <div className="mt-3 flex flex-wrap gap-2">
            <Button type="button" size="sm" disabled={busy}
                    onClick={() => inputRef.current?.click()} data-testid={`branding-${kind}-upload`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              <span className="ml-1.5">{hasImage ? `Replace ${label}` : `Upload ${label}`}</span>
            </Button>
            {hasImage && (
              <Button type="button" size="sm" variant="outline" disabled={busy} onClick={remove}
                      className="text-[#FE3967] border-[#FE3967]/30 hover:bg-[#FE3967]/5" data-testid={`branding-${kind}-remove`}>
                <Trash2 className="h-4 w-4" /><span className="ml-1.5">Remove</span>
              </Button>
            )}
          </div>
          <p className="text-[11px] text-slate-400 mt-2">PNG, JPG, SVG or WebP · up to 8 MB. Select a file from your computer — it's stored securely and quality is preserved.</p>

          <button type="button" onClick={() => setShowUrl((s) => !s)}
                  className="mt-2 inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-600"
                  data-testid={`branding-${kind}-url-toggle`}>
            <ChevronDown className={`h-3 w-3 transition-transform ${showUrl ? "rotate-180" : ""}`} />
            Use an image URL instead (advanced)
          </button>
          {showUrl && (
            <div className="mt-2 flex gap-2">
              <Input value={urlInput} onChange={(e) => setUrlInput(e.target.value)}
                     placeholder="https://…/logo.png" className="h-9 text-sm" data-testid={`branding-${kind}-url-input`} />
              <Button type="button" size="sm" variant="outline" disabled={busy} onClick={applyUrl}
                      data-testid={`branding-${kind}-url-apply`}>Apply</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
